import { useId, type ReactNode } from 'react'

import { cn } from '../../lib/cn'
import { Switch } from '../Switch/Switch'
import s from './SwitchField.module.css'

/**
 * Rangée de réglage (`.set-row` des prototypes), cible entière. Le `<label>` est le **voisin** de
 * l'interrupteur, jamais son parent : imbriqué, le contrôle basculerait deux fois.
 *
 * ⚠️ `id` va à l'`<input>` caché de la primitive, celui que le `<label>` active : le nom — le seul
 * libellé, pas ses deux lignes — et la description se posent donc sur le rôle `switch`.
 */
export interface SwitchFieldProps {
  label: string
  hint?: ReactNode
  icon?: ReactNode
  /** « à venir » : hors du nom, qui dit ce qu'est le contrôle, pas son état. */
  note?: ReactNode
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
  className?: string
}

export function SwitchField({
  label,
  hint,
  icon,
  note,
  checked,
  onCheckedChange,
  disabled,
  className,
}: SwitchFieldProps) {
  const id = useId()
  const labelId = useId()
  const hintId = useId()

  return (
    <div className={cn(s.root, className)}>
      {icon !== undefined && (
        <span className={s.icon} aria-hidden>
          {icon}
        </span>
      )}
      <label className={s.text} htmlFor={id}>
        <span className={s.label}>
          <span id={labelId}>{label}</span>
          {note !== undefined && <span className={s.note}>{note}</span>}
        </span>
        {hint !== undefined && (
          <span className={s.hint} id={hintId}>
            {hint}
          </span>
        )}
      </label>
      <Switch
        id={id}
        className={s.switch}
        aria-labelledby={labelId}
        aria-describedby={hint === undefined ? undefined : hintId}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </div>
  )
}
